import { ChevronDown, PieChart } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Select } from '@unistocks/components/select/select';
import { toast } from '@unistocks/components/toast';
import { AddAssetForm } from '@unistocks/features/portfolios/components/forms/add-asset-form';
import {
  setPortfoliosGetData,
  usePortfoliosAddAssetMutation,
  usePortfoliosGetQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { UNISTOCKS_ARROW } from '@unistocks/shared/consts';
import { t } from '@unistocks/shared/localization/index.ts';
import { isHandledError } from '@unistocks/shared/utils/error';
import { useRouter } from 'expo-router';

type AddAssetProps = {
  id: string;
  sectionId?: string;
};

export const AddAsset: React.FC<AddAssetProps> = ({ id: portfolioId, sectionId }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: portfolio, isSuccess } = usePortfoliosGetQuery(portfolioId);
  const section = portfolio?.sections.find(section => section.id === sectionId);

  const { mutateAsync: addAsset } = usePortfoliosAddAssetMutation(portfolioId, sectionId ?? '', {
    onSuccess: (portfolioDto, input) => {
      setPortfoliosGetData(queryClient, () => portfolioDto, portfolioId);

      toast.success(
        t('::SuccessfullyAddedAsset', { 0: input.name ?? input.assetSearchResult?.name })
      );

      router.back();
    },
    onError: (e, input) => {
      if (isHandledError(e)) {
        toast.error(e.error.message);
      }
    }
  });

  if (isSuccess) {
    return (
      <Page size="medium">
        <Page.ScrollView
          headerTitle={t('::AddWhat', { 0: t('::Holdings').toLowerCase() })}
          headerSubtitle={
            <Select
              isRequired
              options={portfolio?.sections.map(section => ({
                key: section.name,
                value: section.id,
                icon: <Image source={UNISTOCKS_ARROW} />
              }))}
              value={sectionId}
              onChange={value => router.setParams({ sectionId: value })}
              trigger={
                <Row gap={'$1'} alignItems={'center'}>
                  <Page.Heading>
                    {t('::Category')}: {section?.name}
                  </Page.Heading>
                  <ChevronDown color={'$headingFont'} />
                </Row>
              }
              headerTitle={`${t('::AddWhat', { 0: t('::Holdings').toLocaleLowerCase() })} ${t(
                '::AddSuffix'
              )}`}
            />
          }
          headerLogo={false}
          breadcrumbs={[
            {
              key: 'portfolio',
              href: { pathname: '/(tabs)/portfolios/[id]', params: { id: portfolioId } },
              name: t('::Menu:Portfolios'),
              rootPathName: 'portfolios',
              Icon: props => <PieChart {...props} />
            }
          ]}
        >
          <Page.Content>
            <AddAssetForm portfolio={portfolio} onSubmit={addAsset} onCancel={router.back} />
          </Page.Content>
        </Page.ScrollView>
      </Page>
    );
  }

  return null;
};

AddAsset.displayName = 'AddAsset';
